
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { generateGuaranteedUniqueAssessmentId, logAssessmentOperation } from "@/utils/assessmentUtils";

export const useAssessmentHandling = () => {
  const navigate = useNavigate();

  /**
   * Handle creating a completely new risk assessment
   * ALWAYS creates a new unique assessment with blank data
   * Now starts at step 1 to allow naming before questions
   */
  const handleCreateNewAssessment = useCallback(async (projectId: string, folderId: string) => {
    if (!folderId) {
      console.error("No folder ID provided to handleCreateNewAssessment");
      return;
    }
    
    try {
      // Generate guaranteed unique assessment ID for this new assessment
      const newAssessmentId = await generateGuaranteedUniqueAssessmentId(projectId, folderId);
      
      logAssessmentOperation("CREATE_NEW_NAVIGATION", {
        projectId,
        folderId,
        newAssessmentId
      });
      
      // Navigate with createNew=true and start at step 1 for naming/project info
      navigate(`/riskbedomning?projektId=${projectId}&folderId=${folderId}&assessmentId=${newAssessmentId}&createNew=true&steg=1`);
    } catch (error) {
      console.error("Error generating unique assessment ID:", error);
      // Fallback to timestamp-based ID
      const fallbackId = `assessment_${projectId}_${folderId}_${Date.now()}`;
      navigate(`/riskbedomning?projektId=${projectId}&folderId=${folderId}&assessmentId=${fallbackId}&createNew=true&steg=1`);
    }
  }, []);

  /**
   * Handle navigating to an existing assessment
   * Loads specific assessment data without creating new
   */
  const handleNavigateToAssessment = useCallback((assessmentId: string, folderId: string, projectId?: string) => {
    if (!folderId) {
      console.error("No folder ID provided to handleNavigateToAssessment");
      return;
    }
    
    if (!assessmentId) {
      console.error("No assessment ID provided to handleNavigateToAssessment");
      return;
    }
    
    logAssessmentOperation("NAVIGATE_TO_EXISTING", {
      assessmentId,
      folderId,
      projectId
    });
    
    // Determine the correct route based on assessment type
    // SKR assessments can start with 'skr_' or 'skr_assessment_'
    const isSKRAssessment = assessmentId.startsWith('skr_assessment_') || assessmentId.startsWith('skr_');
    const basePath = isSKRAssessment ? '/skr-bedomning' : '/riskbedomning';
    
    logAssessmentOperation("ROUTE_DETERMINATION", {
      assessmentId,
      isSKRAssessment,
      basePath,
      startsWithSkr: assessmentId.startsWith('skr_'),
      startsWithSkrAssessment: assessmentId.startsWith('skr_assessment_')
    });
    
    // Navigate to specific existing assessment and start at step 3 (questions)
    const url = projectId 
      ? `${basePath}?projektId=${projectId}&folderId=${folderId}&assessmentId=${assessmentId}&startAtSteg3=true`
      : `${basePath}?folderId=${folderId}&assessmentId=${assessmentId}&startAtSteg3=true`;
    
    navigate(url);
  }, [navigate]);

  /**
   * Handle starting a risk assessment (legacy compatibility)
   * Determines whether to create new or open most recent
   */
  const handleStartAssessment = useCallback((projectId: string, folderId: string) => {
    if (!folderId) {
      console.error("No folder ID provided to handleStartAssessment");
      return;
    }
    
    logAssessmentOperation("START_ASSESSMENT_LEGACY", {
      projectId,
      folderId
    });
    
    // For legacy compatibility, this will try to load most recent
    // or create new if none exists, starting at step 3
    navigate(`/riskbedomning?projektId=${projectId}&folderId=${folderId}&startAtSteg3=true`);
  }, [navigate]);

  return {
    handleStartAssessment,
    handleNavigateToAssessment,
    handleCreateNewAssessment
  };
};
